import React, { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { useList, useDeleteOne, useBulkDelete } from "@/hooks/useApi";
import { useAuth } from "@/context/AuthContext";
import { useQueryClient } from "@tanstack/react-query";
import { PageHeader, Loading, EmptyState, StatusBadge, ProgressBar, fmt } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";

export default function Printing() {
  const { canWrite, user } = useAuth();
  const qc = useQueryClient();
  const delOne = useDeleteOne((id) => `/printing/${id}`, ["printing"]);
  const clearAll = useBulkDelete((j) => `/printing/${j.id}`, ["printing"]);
  const { data: jobs, isLoading } = useList("printing", "/printing");
  const { data: orders = [] } = useList("orders", "/orders");
  const { data: types = [] } = useList("printing_types", "/printing_types");
  const { data: machines = [] } = useList("machines", "/machines");
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ order_ref: "", design_name: "", printing_type: "", num_colors: "", machine_name: "", operator: "", target_quantity: "", printed_quantity: "", rejection_quantity: "", status: "In Progress" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!f.order_ref) return toast.error("Select order");
    const o = orders.find((x) => x.id === f.order_ref);
    try {
      await api.post("/printing", { ...f, order_id: o?.order_id, product_name: o?.product_name, num_colors: Number(f.num_colors) || 0, target_quantity: Number(f.target_quantity) || 0, printed_quantity: Number(f.printed_quantity) || 0, rejection_quantity: Number(f.rejection_quantity) || 0 });
      toast.success("Printing job created"); qc.invalidateQueries(); setOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (isLoading) return <Loading />;
  return (
    <div>
      <PageHeader title="Printing Department" subtitle="Screen, DTF, sublimation & specialty print jobs" testid="printing-header">
        {canWrite("production") && <Button onClick={() => setOpen(true)} className="gap-2" data-testid="new-printing-btn"><Plus className="w-4 h-4" /> New Print Job</Button>}
      </PageHeader>
      <Card className="overflow-hidden">
        {!jobs?.length ? <EmptyState title="No printing jobs" /> : (
          <div className="overflow-x-auto"><Table>
            <TableHeader><TableRow>
              <TableHead>Job</TableHead><TableHead>Order</TableHead><TableHead>Design</TableHead><TableHead>Type</TableHead>
              <TableHead>Colors</TableHead><TableHead className="w-40">Progress</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>{jobs.map((j) => {
              const pct = j.target_quantity ? Math.min(100, Math.round(j.printed_quantity / j.target_quantity * 100)) : 0;
              return (
                <TableRow key={j.id} data-testid={`printing-row-${j.job_id}`}>
                  <TableCell className="font-num font-semibold text-primary">{j.job_id}</TableCell>
                  <TableCell className="font-num">{j.order_id}</TableCell>
                  <TableCell className="text-sm">{j.design_name}</TableCell>
                  <TableCell className="text-sm">{j.printing_type}</TableCell>
                  <TableCell className="font-num text-center">{j.num_colors}</TableCell>
                  <TableCell><div className="flex items-center gap-2"><ProgressBar value={pct} /><span className="text-xs font-num w-9">{pct}%</span></div></TableCell>
                  <TableCell><StatusBadge status={j.status} /></TableCell>
                  <TableCell>{canWrite("production") && <Button size="icon" variant="ghost" onClick={() => delOne(j.id, "Delete this print job?")} data-testid={`delete-printing-${j.job_id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>}</TableCell>
                </TableRow>
              );
            })}</TableBody>
          </Table></div>
        )}
      </Card>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="printing-form">
          <DialogHeader><DialogTitle>New Printing Job</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Order</Label>
              <Select value={f.order_ref} onValueChange={(v) => set("order_ref", v)}>
                <SelectTrigger data-testid="printing-order-select"><SelectValue placeholder="Order" /></SelectTrigger>
                <SelectContent>{orders.map((o) => <SelectItem key={o.id} value={o.id}>{o.order_id} — {o.product_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Design Name</Label><Input value={f.design_name} onChange={(e) => set("design_name", e.target.value)} /></div>
            <div><Label>Printing Type</Label>
              <Select value={f.printing_type} onValueChange={(v) => set("printing_type", v)}>
                <SelectTrigger data-testid="printing-type-select"><SelectValue placeholder="Type" /></SelectTrigger>
                <SelectContent>{types.map((t) => <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label># Colors</Label><Input type="number" value={f.num_colors} onChange={(e) => set("num_colors", e.target.value)} /></div>
            <div><Label>Machine</Label>
              <Select value={f.machine_name} onValueChange={(v) => set("machine_name", v)}>
                <SelectTrigger><SelectValue placeholder="Machine" /></SelectTrigger>
                <SelectContent>{machines.map((m) => <SelectItem key={m.id} value={m.name}>{m.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Operator</Label><Input value={f.operator} onChange={(e) => set("operator", e.target.value)} /></div>
            <div><Label>Target Qty</Label><Input type="number" value={f.target_quantity} onChange={(e) => set("target_quantity", e.target.value)} /></div>
            <div><Label>Printed Qty</Label><Input type="number" value={f.printed_quantity} onChange={(e) => set("printed_quantity", e.target.value)} /></div>
            <div><Label>Rejection</Label><Input type="number" value={f.rejection_quantity} onChange={(e) => set("rejection_quantity", e.target.value)} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={submit} data-testid="printing-save-btn">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
